import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import upload from "assets/images/v2/upload-icon.svg";
import styles from "./Documents.module.scss";
import DocumentCard from "components/v2/document-card/DocumentCard";
import docErrorImg from "assets/images/v2/error-illustration.svg";
import noDocumentImg from "assets/images/v2/no-documents.svg";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import {
  dashboardApi,
  useRetrieveNeedsQuery,
  RetrieveNeedDto,
  RetrieveDocumentsResponseDto,
  DocumentStatus,
  useRetrievePalContentQuery,
  RetrievePalResponseDto,
  useViewedDocumentsLogMutation,
} from "pages/dashboard/home/dashboardService";
import { getCurrentUserId, getLoanId, getNhDealApplicationId, setLogout } from "features/authSlice";
import { Dropdown, DropdownOption } from "components/v2/dropdown/Dropdown";
import { SORT_BY_DATE, SORT_BY_TITLE } from "utilities/Constant";
import TogglePills from "components/v2/toggle-pills/TogglePills";
import useDesktopChecker from "hooks/useDesktopChecker";
import SearchBar from "components/v2/search-bar/SearchBar";
import { LoadingPageProps } from "../loadingPageProps";
import Drawer, { DrawerType } from "components/ui/drawer/Drawer";
import { Spinner } from "components/v2/spinner/spinner";
import FileUploadPanel from "components/v2/file-upload-panel/FileUploadPanel";
import DocumentPreview from "components/v2/document-preview/DocumentPreview";
import searchNotFoundImg from "assets/images/v2/search-not-found.svg";
const Documents: React.FC<LoadingPageProps & { pendingDocumentId?: string | null; onDocumentOpened?: () => void }> = ({
  onLoad,
  pendingDocumentId,
  onDocumentOpened,
}: LoadingPageProps & { pendingDocumentId?: string | null; onDocumentOpened?: () => void }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [fileInfo, setFileInfo] = useState({ filePath: "", fileName: "", documentId: "" });
  const [needsByUser, setNeedsByUser] = useState<RetrieveNeedDto[]>([]);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const [invokeDocumentContent, { isLoading: isDocumentLoading, isFetching: isDocumentFetching, isError: isDocumentError }] =
    dashboardApi.endpoints.retrieveDocumentContent.useLazyQuery();
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const loanId = useSelector(getLoanId);
  const [documentsByNaf, setDocumentsByNaf] = useState<RetrievePalResponseDto[]>([]);
  const isDesktop = useDesktopChecker();
  const [fileName, setFileName] = useState("");
  const [selectedDocument, setSelectedDocument] = useState<RetrieveDocumentsResponseDto | null>(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [updateViewedDocuments] = useViewedDocumentsLogMutation();
  const userId = useSelector(getCurrentUserId);

  const {
    data: retrievePalData,
    isLoading: isRetreivePalLoading,
    isSuccess: isRetreivePalSuccess,
  } = useRetrievePalContentQuery(
    { nhDealApplicationId, loanId },
    {
      skip: !featureFlag?.preApprovalLetterEnabled || loanId === "",
    }
  );

  const documentFilters = [
    { label: isDesktop ? "All Documents" : "All", value: "all" },
    { label: "NAF Documents", value: "naf" },
    { label: "Your Documents", value: "user" },
  ] as const;
  type FilterType = typeof documentFilters[number]["value"]; // 'all' | 'naf' | 'user'

  const [filter, setFilter] = useState<FilterType>("all");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [sortField, setSortField] = useState<string>(SORT_BY_DATE);
  const [disableSort, setDisableSort] = useState<boolean>(true);
  const [disableUploadBtn, setDisableUploadBtn] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [documentLoadError, setDocumentLoadError] = useState(false);
  const {
    data: retrieveNeedsData,
    isLoading: isRetrieveNeedsLoading,
    isSuccess: isRetrieveNeedsSuccess,
    isError: isRetrieveNeedsError,
  } = useRetrieveNeedsQuery(nhDealApplicationId, {
    skip: !nhDealApplicationId || !featureFlag?.needsListV2Enabled,
  });

  useEffect(() => {
    if (!isRetrieveNeedsLoading && isRetrieveNeedsSuccess && retrieveNeedsData) {
      setNeedsByUser(retrieveNeedsData);
    }
  }, [isRetrieveNeedsLoading, isRetrieveNeedsSuccess, retrieveNeedsData]);

  useEffect(() => {
    if (loanId && !isRetreivePalLoading && isRetreivePalSuccess && retrievePalData !== documentsByNaf) {
      setDocumentsByNaf(retrievePalData);
    }
  }, [isRetreivePalLoading, isRetreivePalSuccess, loanId]);

  const transformNeeds = (needs: RetrieveNeedDto[]): RetrieveDocumentsResponseDto[] =>
    needs
      .flatMap((need) => need.documents)
      .map((doc) => ({
        fileId: doc.id,
        name: doc.docName ?? "",
        documentPath: "",
        dateCreated: doc.uploadedDateTime ?? "",
        category: "",
        userName: `${doc.uploader.firstName} ${doc.uploader.lastName}`,
        status: doc.status,
        invalidReason: doc.invalidReason ?? "",
        detailedExplanation: doc.detailedExplanation ?? "",
        userNotified: doc.userNotified,
      }));

  const transformDocuments = (documents: RetrievePalResponseDto[]): RetrieveDocumentsResponseDto[] => {
    return (documents || []).map((doc: RetrievePalResponseDto) => ({
      fileId: doc.palInstanceId,
      name: doc.documentContent?.name ?? "",
      documentPath: doc.documentContent?.fileContents ?? "",
      dateCreated: doc.createdDate ?? "",
      category: "",
    }));
  };

  const isDocumentMarkedInvalid = (doc: RetrieveDocumentsResponseDto): boolean =>
    doc.status?.toLowerCase() === DocumentStatus.Rejected.toLowerCase() && !!doc.invalidReason && !!doc.detailedExplanation && !!doc.userNotified;

  const getBlob = async (document: RetrieveDocumentsResponseDto): Promise<string> => {
    try {
      const TIMEOUT_MS = 30000;
      const timeoutPromise = new Promise<never>((_, reject) => setTimeout(() => reject(new Error("Request timed out")), TIMEOUT_MS));
      const response = await Promise.race([invokeDocumentContent(document.fileId), timeoutPromise]);
      const { data, isError, error } = response;

      if (isError && error && typeof error === "object" && "status" in error && (error as { status?: number }).status === 401) {
        dispatch(setLogout());
        navigate("/", { replace: true });
        return "";
      }

      if (data) {
        const blob = new Blob([Uint8Array.from(window.atob(data.fileContents), (c) => c.charCodeAt(0))], { type: data.contentType });
        const url = URL.createObjectURL(blob);
        return url;
      }
    } catch (error) {
      console.error("Error getting document blob:", error);
      return "";
    }

    return "";
  };

  const getPathUrl = (document: RetrieveDocumentsResponseDto, contentType?: string) => {
    if (document) {
      const blob = new Blob([Uint8Array.from(window.atob(document.documentPath), (c) => c.charCodeAt(0))], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      void updateViewedDocuments({ userId, documentIds: [document.fileId] });
      return url;
    } else {
      return "";
    }
  };

  const handleDocumentViewer = (path: string, name: string, documentRefId: string) => {
    setFileInfo({ filePath: path, fileName: name, documentId: documentRefId });
  };

  const handlePreviewDocument = async (doc: RetrieveDocumentsResponseDto) => {
    setFileName(doc.name);
    setShow(true);
    setSelectedDocument(doc);
    if(documentsByNaf){
      const nafDoc = documentsByNaf.find((d) => d.palInstanceId === doc.fileId);
      if(nafDoc){
        const url = getPathUrl(doc, nafDoc.documentContent?.contentType);
        if (url) handleDocumentViewer(url, doc.name, doc.fileId);
        return;
      }
    }
    const url = await getBlob(doc);
    if (url)
      {
      handleDocumentViewer(url, doc.name, doc.fileId);
    }
    else {
      setDocumentLoadError(true);
    }
  };

  const handleDownload = async (doc: RetrieveDocumentsResponseDto | null) => {
    if (!doc) return;
    let fileUrl = fileInfo.filePath;
    if (!fileUrl) {
      if (documentsByNaf) {
        const nafDoc = documentsByNaf.find((d) => d.palInstanceId === doc.fileId);
        if (nafDoc) {
          fileUrl = getPathUrl(doc, nafDoc.documentContent?.contentType);
        } else {
          fileUrl = await getBlob(doc);
        }
      }

      if (!fileUrl) return;
      setSelectedDocument((prev) => (prev && prev.fileId === doc.fileId ? { ...prev, url: fileUrl } : prev));
    }

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = doc.name;
    link.target = "_blank";
    link.rel = "noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const docs = useMemo(() => [{ uri: fileInfo.filePath, fileName: fileInfo.fileName }], [fileInfo]);
  const [documents, setDocuments] = useState<RetrieveDocumentsResponseDto[]>([]);
  useEffect(() => {
    setDocuments(featureFlag?.needsListV2Enabled ? transformNeeds(needsByUser) : []);
  }, [featureFlag?.needsListV2Enabled, needsByUser]);

  useEffect(() => {
    if (pendingDocumentId && documentsByNaf.length > 0) {
      const nafDocuments = transformDocuments(documentsByNaf);
      const targetDocument = nafDocuments.find((doc) => doc.fileId === pendingDocumentId);
      if (targetDocument) {
        void handlePreviewDocument(targetDocument);
        onDocumentOpened?.();
      }
    }
  }, [pendingDocumentId, documentsByNaf, handlePreviewDocument, onDocumentOpened, isRetreivePalLoading, isRetreivePalSuccess]);

  useEffect(() => {
    if (!isRetrieveNeedsLoading && !isRetreivePalLoading) {
      onLoad?.();
    }
  }, [isRetrieveNeedsLoading, isRetreivePalLoading, onLoad]);

  const dropdownOptions: DropdownOption[] = [
    {
      title: SORT_BY_DATE,
      isSelected: sortField === SORT_BY_DATE,
    },
    {
      title: SORT_BY_TITLE,
      isSelected: sortField === SORT_BY_TITLE,
    },
  ];

  const handleSort = (option: DropdownOption) => {
    if (sortField !== option.title) {
      setSortField(option.title);
      setSortOrder("asc");
    }
  };

  const filteredDocuments = useMemo(() => {
    // Build base set according to selected category filter
    let base: RetrieveDocumentsResponseDto[] = [];
    if (filter === "naf") {
      base = transformDocuments(documentsByNaf) || [];
    } else if (filter === "user") {
      base = documents || [];
    } else if (filter === "all") {
      base = (documents || []).concat((transformDocuments(documentsByNaf) || []));
    }

    // Apply search filtering (case-insensitive) within the selected category
    const query = searchQuery?.trim().toLowerCase() ?? "";
    let filtered = base;
    if (query.length > 0) {
      filtered = base?.filter((doc) => {
        const name = (doc.name || "").toLowerCase();
        return name.includes(query);
      });
    }

    // Apply sorting to the filtered results
    filtered.sort((a, b) => {
      let comparison = 0;

      if (sortField === SORT_BY_DATE) {
        comparison = new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime();
      } else if (sortField === SORT_BY_TITLE) {
        comparison = (a.name || "").localeCompare(b.name || "");
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    setDisableSort(filtered.length == 0);
    setDisableUploadBtn(filter === "naf");
    return filtered;
  }, [filter, sortField, sortOrder, documents, documentsByNaf, searchQuery]);

  const renderModal = () => {
    const isLoading = isDocumentLoading || isDocumentFetching;
    const hasFilePath = Boolean(fileInfo.filePath);

    return (
      <Drawer
        isOpen={show}
        onClose={() => setShow(false)}
        title={fileName}
        customCss={"preview-document"}
        handleDownload={() => handleDownload(selectedDocument)}
        hasError={isDocumentError}
        type={DrawerType.DocumentPreview}
        invalidReason={selectedDocument?.invalidReason}
        invalidDescription={selectedDocument?.detailedExplanation}
        centerContent={isLoading}
      >
        {isLoading && (
          <>
            <Spinner isApi />
            <span className={styles.spinnerText}>Loading your document This will just take a moment</span>
          </>
        )}
        {!isLoading && hasFilePath && (
          <DocumentPreview
            fileUrl={fileInfo.filePath}
            fileName={fileInfo.fileName}
            handleDownload={() => handleDownload(selectedDocument)}
          />
        )}
        {((!isLoading && !hasFilePath) || documentLoadError) && (
          <div className={styles.previewError}>
            <img src={docErrorImg} alt="Error loading documents" className={styles.errorImage} />
            <p className={styles.errorText}>
              Something went wrong and we couldn't display your documents. Please try again later. We apologize for the inconvenience!
            </p>
          </div>
        )}
      </Drawer>
    );
  };
  const openDocumentUploadDrawer = () => {
    if(disableUploadBtn) return;
    setShowDrawer(true);
  };

  const renderDocumentUploadDrawer = () => {
    const need = retrieveNeedsData?.find((n) => n.isDefault === true);
    return (
      <Drawer
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        type={DrawerType.DocumentUpload}
        title={need?.title}
        subTitle="You can upload additional files at any time, up to 12 files per upload."
        customCss="drawer-document-upload"
      >
        <>{need?.id && <FileUploadPanel needId={need.id} handleByAction={() => setShowDrawer(false)} />}</>
      </Drawer>
    );
  };

  const renderDocuments = () => {
    if (isRetrieveNeedsLoading) {
      return (
        <div className={styles.docLoadingContainer}>
          <Spinner isApi />
          <span className={styles.spinnerText}>Loading your document This will just take a moment</span>
        </div>
      )
    }


    if (isRetrieveNeedsError) {
      return (
        <div className={styles.errorContainer}>
          <img src={docErrorImg} alt="Error loading documents" className={styles.errorImage} />
          <p className={styles.errorText}>We are having issues loading your documents. Try again later.</p>
        </div>
      )
    } else if (filteredDocuments.length === 0 && searchQuery.trim().length === 0) {
      return (
        <div className={styles.noDocContainer}>
          <img src={noDocumentImg} alt="Click Upload to add a file" className={styles.errorImage} />
          <p className={styles.clickToAddDocumentText}>Click Upload to add a file</p>
        </div>
      )
    }
    else if (filteredDocuments.length === 0 && searchQuery.trim().length > 0) {
      return (
        <div className={styles.noDocContainer}>
          <img src={searchNotFoundImg} alt="No documents found" className={styles.searchNotFoundImg} />
          <p className={styles.noDocumentsFoundText}>No documents found</p>
        </div>
      )
    } else {
      return filteredDocuments.map((doc) => (
        <DocumentCard
          documentId={doc.fileId}
          key={doc.fileId}
          documentName={doc.name}
          documentInfo={doc.dateCreated}
          isInvalidDocument={isDocumentMarkedInvalid(doc)}
          handleDocumentViewer={() => handlePreviewDocument(doc)}
          handleDownLoad={() => handleDownload(doc)}
        />
      ));
    }
  }

  useEffect(() => {
    onLoad?.();
  }, []);

  return (
    <div className={styles.content}>
      <div className={styles.documents}>Documents</div>
      <div className={styles.viewSearchFor}>View, search for, and sort your application documents.</div>
      <SearchBar placeholder="Search documents" initialValue={searchQuery} onSearch={setSearchQuery} />
      <TogglePills options={documentFilters} value={filter} onChange={setFilter} />
      <div className={styles.btnActions}>
        <Dropdown options={dropdownOptions} disable={disableSort} onSelection={handleSort} onToggleOrder={() =>
          setSortOrder(prev => (prev === "asc" ? "desc" : "asc"))
        } />
        <div className={`${styles.uploadWrapper} ${disableUploadBtn ? styles.uploadWrapperDisabled : ""}`}>
          <div className={styles.upload} onClick={openDocumentUploadDrawer}>
            <div className={styles.uploadIcon}>
              <img className={styles.uploadPrimaryIcon} alt="upload" src={upload} />
            </div>
            <div className={styles.uploadPhrase}>Upload</div>
          </div>
        </div>
      </div>
      <div className={styles.documentCard}>{renderDocuments()}</div>
      {show && renderModal()}
      {showDrawer && renderDocumentUploadDrawer()}
    </div>
  );
};

export default Documents;
